/**
 * Expiring Contracts Table Component
 * US-CONT-005: View Expiring Contracts Alert
 * Full table with sortable headers and pagination
 */

import { ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';
import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type {
  ContractAction,
  ContractSort,
  ExpiringContractWithUrgency
} from '../types/expiringContracts.types';
import { ExpiringContractsTableRow, ExpiringContractsTableRowSkeleton } from './ExpiringContractsTableRow';
import { ContractUrgencyBadge } from './ContractUrgencyBadge';
import { formatExpiryDate } from '../utils/contractFormatters.utils';

interface ExpiringContractsTableProps {
  contracts: ExpiringContractWithUrgency[];
  isLoading: boolean;
  sort: ContractSort;
  onSortChange: (sort: ContractSort) => void;
  onAction: (action: ContractAction) => void;
  onRowClick?: (contract: ExpiringContractWithUrgency) => void;
  className?: string;
}

interface SortableHeaderProps {
  field: ContractSort['field'];
  label: string;
  currentSort: ContractSort;
  onSortChange: (sort: ContractSort) => void;
  className?: string;
  align?: 'left' | 'center' | 'right';
}

function SortableHeader({
  field,
  label,
  currentSort,
  onSortChange,
  className,
  align = 'left'
}: SortableHeaderProps) {
  const isActive = currentSort.field === field;
  const direction = isActive ? currentSort.direction : null;

  function handleClick() {
    if (isActive) {
      // Toggle direction
      onSortChange({
        field,
        direction: direction === 'asc' ? 'desc' : 'asc'
      });
    } else {
      // New field, default to ascending
      onSortChange({
        field,
        direction: 'asc'
      });
    }
  }

  return (
    <TableHead className={cn(className, align === 'right' && 'text-right')}>
      <Button
        variant="ghost"
        size="sm"
        onClick={handleClick}
        className={cn(
          '-ml-3 h-8 data-[state=open]:bg-accent',
          align === 'right' && 'ml-auto'
        )}
      >
        <span>{label}</span>
        {isActive ? (
          direction === 'asc' ? (
            <ArrowUp className="ml-2 h-4 w-4" />
          ) : (
            <ArrowDown className="ml-2 h-4 w-4" />
          )
        ) : (
          <ArrowUpDown className="ml-2 h-4 w-4 opacity-50" />
        )}
      </Button>
    </TableHead>
  );
}

export function ExpiringContractsTable({
  contracts,
  isLoading,
  sort,
  onSortChange,
  onAction,
  onRowClick,
  className
}: ExpiringContractsTableProps) {
  return (
    <div className={cn('rounded-md border', className)}>
      <Table>
        <TableHeader>
          <TableRow>
            <SortableHeader
              field="customerName"
              label="Customer"
              currentSort={sort}
              onSortChange={onSortChange}
            />

            <SortableHeader
              field="contractNumber"
              label="Contract #"
              currentSort={sort}
              onSortChange={onSortChange}
            />

            <TableHead className="hidden md:table-cell">Type</TableHead>

            <SortableHeader
              field="contractValue"
              label="Value"
              currentSort={sort}
              onSortChange={onSortChange}
              className="hidden lg:table-cell"
              align="right"
            />

            <SortableHeader
              field="endDate"
              label="Expiry Date"
              currentSort={sort}
              onSortChange={onSortChange}
            />

            <TableHead>Urgency</TableHead>

            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>

        <TableBody>
          {isLoading ? (
            // Show 5 skeleton rows while loading
            <>
              {Array.from({ length: 5 }).map((_, index) => (
                <ExpiringContractsTableRowSkeleton key={index} />
              ))}
            </>
          ) : contracts.length === 0 ? (
            // Empty state
            <TableRow>
              <td colSpan={7} className="h-24 text-center">
                <p className="text-muted-foreground">No contracts found</p>
              </td>
            </TableRow>
          ) : (
            // Data rows
            contracts.map((contract) => (
              <ExpiringContractsTableRow
                key={contract.id}
                contract={contract}
                onAction={onAction}
                onRowClick={onRowClick}
              />
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
}

/**
 * Compact table for mobile views
 */
interface CompactTableProps {
  contracts: ExpiringContractWithUrgency[];
  isLoading: boolean;
  onAction: (action: ContractAction) => void;
  onRowClick?: (contract: ExpiringContractWithUrgency) => void;
  className?: string;
}

export function CompactContractsTable({
  contracts,
  isLoading,
  onAction,
  onRowClick,
  className
}: CompactTableProps) {
  if (isLoading) {
    return (
      <div className={cn('space-y-2', className)}>
        {Array.from({ length: 3 }).map((_, index) => (
          <div key={index} className="border rounded-lg p-4 space-y-3">
            <div className="h-4 w-3/4 bg-muted animate-pulse rounded" />
            <div className="h-3 w-1/2 bg-muted animate-pulse rounded" />
            <div className="h-6 w-20 bg-muted animate-pulse rounded" />
          </div>
        ))}
      </div>
    );
  }

  if (contracts.length === 0) {
    return (
      <div className="border rounded-lg p-8 text-center">
        <p className="text-muted-foreground">No contracts found</p>
      </div>
    );
  }

  return (
    <div className={cn('space-y-2', className)}>
      {contracts.map((contract) => (
        <div
          key={contract.id}
          className="border rounded-lg p-4 space-y-2 cursor-pointer hover:bg-muted/50 transition-colors"
          onClick={() => onRowClick?.(contract)}
        >
          <div className="flex justify-between items-start">
            <div>
              <p className="font-medium">{contract.customerName}</p>
              <p className="text-sm text-muted-foreground">{contract.contractNumber}</p>
            </div>
            <ContractUrgencyBadge
              urgency={contract.urgency.urgencyLevel}
              daysRemaining={contract.urgency.daysRemaining}
              showLabel={false}
              size="sm"
            />
          </div>
          <p className="text-sm text-muted-foreground">
            Expires: {formatExpiryDate(contract.endDate)}
          </p>
        </div>
      ))}
    </div>
  );
}
